// Front-end for the Partner page — the hero's headline sweep, the hero art's Lottie,
// and scroll-reveal.
// Peer Vite entry — never imports the page SCSS.

// lottie_light, as CLAUDE.md requires. business-handshake.json is shape layers only —
// no expressions, no text layers, no images — so nothing that build drops is used.
import lottie, { type AnimationItem } from 'lottie-web/build/player/lottie_light';
import { prefersReducedMotion } from '../Assets/motion';
import { revealOnScroll } from '../Assets/scrollReveal';

// The headline's typewriter sweep, as on the reference page: the highlight fills in
// one character at a time with a text cursor riding its leading edge.
//
// Progressive enhancement, and the order matters. The markup ships the phrase already
// highlighted and the cursor already at its end — the finished state — so with this
// script absent, blocked, or skipped for reduced motion, the headline is correct and
// simply does not animate. Everything below only replays how it got there.
const ms = 42; // per character; ~1.5s over the reference's phrase length

const sweep = (): void => {
    const title = document.querySelector<HTMLElement>('.pt-hero .title');
    const hl = title?.querySelector<HTMLElement>('.hl');
    const caret = title?.querySelector<HTMLElement>('.caret');
    if (!title || !hl || !caret) return;

    // One span per character, including the spaces — a highlighted space is what keeps
    // the band continuous between words. Plain inline spans, so the phrase still wraps
    // and breaks exactly where the un-split text did.
    const text = hl.textContent ?? '';
    hl.textContent = '';
    const chars = Array.from(text, (character) => {
        const span = document.createElement('span');
        span.className = 'ch';
        span.textContent = character;
        hl.append(span);
        return span;
    });
    if (chars.length === 0) return;

    hl.classList.add('sweeping');

    // The cursor rides as an absolutely positioned CLONE while the original stays in
    // the text, hidden. Two reasons: the original holds its 4px of the line so nothing
    // reflows when the clone is dropped, and moving a clone by transform keeps the
    // whole sweep off the layout path.
    //
    // Offsets are read ONCE, here, against .title (the spans' offsetParent). The text
    // is already laid out and the sweep changes only colours, so re-reading them per
    // step would be a layout flush per character for values that cannot have changed.
    const spots = chars.map((span) => ({
        x: span.offsetLeft + span.offsetWidth,
        y: span.offsetTop,
    }));

    const rider = caret.cloneNode(true) as HTMLElement;
    rider.classList.add('rider');
    caret.classList.add('riding');
    title.append(rider);

    let i = 0;
    const timer = window.setInterval(() => {
        const spot = spots[i];
        if (!spot) {
            // Done: hand the cursor back to the one that never left the text.
            window.clearInterval(timer);
            rider.remove();
            caret.classList.remove('riding');
            return;
        }
        chars[i]?.classList.add('on');
        rider.style.transform = `translate(${spot.x}px, ${spot.y}px)`;
        i += 1;
    }, ms);
};

// Embedded mode (the Android app's WebView) drops the site's entrance animations, and
// scrollReveal already checks both conditions for itself — this one has to ask.
if (!prefersReducedMotion() && !document.body.classList.contains('embedded')) {
    sweep();
}

// The hero art — 600×600, 180 frames at 30fps, authored as one seamless 6s loop —
// mounted into .stage from the JSON named by data-lottie.
//
// Mounted on first sight rather than on load, because .art is display: none on phones
// and a box that is not rendered never intersects: a phone never fetches the JSON or
// runs a player it cannot see, and a window widened past sm mounts it the moment the
// art appears. The same observer pauses it whenever it is off-screen — lottie-web keeps
// animating a player nobody can see, and this one sits above a long page.
//
// Reduced motion loads it but never plays it, which holds frame 0: the whole scene, at
// rest. This is content, not an entrance animation, so embedded mode plays it as the
// home hero does.
const stage = document.querySelector<HTMLElement>('.pt-hero .art .stage');
const source = stage?.dataset.lottie;

if (stage && source) {
    let animation: AnimationItem | undefined;

    // One callback can carry several queued entries for this one target, oldest first —
    // a fast scroll out and back, say — so only the last is the stage's current state.
    new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        if (!entry.isIntersecting) {
            animation?.pause();
            return;
        }
        if (!animation) {
            animation = lottie.loadAnimation({
                container: stage,
                renderer: 'svg',
                loop: true,
                autoplay: false,
                path: source,
            });
        }
        // Safe before the JSON lands: the player will not advance a frame until it has
        // loaded, and then starts from here.
        if (!prefersReducedMotion()) {
            animation.play();
        }
    }).observe(stage);
}

revealOnScroll(
    '.pt-hero .copy > *, .pt-hero .art, '
    + '.pt-latest .intro, .pt-latest .pcard, '
    + '.pt-start .band',
);
